import { useState, type ReactNode } from 'react';
import {
  patternOf,
  squareName,
  type Card,
  type Constants,
  type Epitaph,
  type Inscription,
  type MatchResult,
  type MatchView,
  type RoundSummary,
  type Side,
  type Square,
} from '../../engine';
import { t } from '../../i18n';
import { cardLong, routeWords, type Names } from '../../app/describe';
import { CardView } from './CardView';
import { Modal } from './Modal';
import { PawnFigure } from './PawnFigure';
import { PileCard, pileEntryLabel } from './PilePanel';
import { WinPips } from './PlayerStrip';
import { RouteGlyph } from './RouteGlyph';
import { RouteSteps } from './RouteSteps';
import { ShareButton, type ShareResult } from './ShareLink';
import { kitItems } from './LedgerStrip';
import { ItemSheet, useBrowser } from './night/Item';
import { Mantel } from './night/Mantel';
import { NightObject } from './night/Objects';

function RouteBlock({
  ins,
  owner,
  flip,
  title,
}: {
  ins: Inscription;
  owner: Side;
  flip: boolean;
  title: string;
}) {
  return (
    <div className="route-block">
      <span className="mini-label">{title}</span>
      <RouteGlyph links={ins.links} size={56} flip={flip} />
      <RouteSteps links={ins.links} owner={owner} />
      <span className="dim">{t('epitaph.score', { score: ins.recordedScore })}</span>
    </div>
  );
}

export function StarterDialog({
  round,
  opponentName,
  onChoose,
}: {
  round: number;
  opponentName: string;
  onChoose: (me: boolean) => void;
}) {
  return (
    <Modal
      title={t('dialog.starter.title', { round })}
      footer={
        <>
          <button
            type="button"
            className="btn btn-primary"
            data-autofocus
            onClick={() => onChoose(true)}
          >
            {t('dialog.starter.me')}
          </button>
          <button type="button" className="btn" onClick={() => onChoose(false)}>
            {t('dialog.starter.them', { name: opponentName })}
          </button>
        </>
      }
    >
      <p>{t('dialog.starter.body')}</p>
    </Modal>
  );
}

export function InscriptionDialog({
  current,
  next,
  pawnName,
  owner,
  flip,
  onChoose,
}: {
  current: Inscription;
  next: Inscription;
  pawnName: string;
  owner: Side;
  flip: boolean;
  onChoose: (replace: boolean) => void;
}) {
  return (
    <Modal
      title={`${t('dialog.inscription.title')}: ${pawnName}`}
      wide
      footer={
        <>
          <button type="button" className="btn" data-autofocus onClick={() => onChoose(false)}>
            {t('action.keep')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onChoose(true)}>
            {t('action.replace')}
          </button>
        </>
      }
    >
      <p className="epitaph-why">{t('dialog.inscription.body', { pawn: pawnName })}</p>
      <div className="route-compare">
        <RouteBlock ins={current} owner={owner} flip={flip} title={t('inscription.current')} />
        <RouteBlock ins={next} owner={owner} flip={flip} title={t('inscription.new')} />
      </div>
    </Modal>
  );
}

/**
 * The Epitaph keep choice (§10.3). It comes on the opponent's turn, right after the move that
 * captured the pawns, so it says what happened and why the choice is needed now, and shows each
 * route in words: what it does from the player's side, and the multiplier it replays at.
 */
export function EpitaphChoiceDialog({
  candidates,
  held,
  capturedOn,
  opponentName,
  constants,
  names,
  onKeep,
}: {
  /** The Epitaphs still to choose from. */
  candidates: readonly Epitaph[];
  /** Ids of the Epitaphs the player held before this move. */
  held: readonly string[];
  /** Where a pawn was captured, when the log says. */
  capturedOn: (pawnId: string) => Square | null;
  opponentName: string;
  constants: Pick<Constants, 'EPITAPH_SLOTS' | 'BONUS_LADDER' | 'BONUS_ECHO' | 'BONUS_STREAK'>;
  names: Names;
  onKeep: (id: string) => void;
}) {
  const arrived = candidates.filter((e) => !held.includes(e.id)).length;
  const slots = constants.EPITAPH_SLOTS;
  const why = [
    arrived === 1
      ? t('dialog.epitaph.arrived.one', { opponent: opponentName })
      : t('dialog.epitaph.arrived.many', { opponent: opponentName, n: arrived }),
    slots > 1
      ? t('dialog.epitaph.slots', { n: slots })
      : held.length > 0
        ? t('dialog.epitaph.slotHeld')
        : t('dialog.epitaph.slot'),
    t('dialog.epitaph.then', { opponent: opponentName }),
  ];
  return (
    <Modal title={t('dialog.epitaph.title')} className="epitaph-choice">
      <p className="epitaph-why">{why.join(' ')}</p>
      <ul className="epitaph-options">
        {candidates.map((e, i) => {
          const links = e.inscription.links;
          const square = capturedOn(e.sourcePawnId);
          const origin = held.includes(e.id)
            ? t('dialog.epitaph.held')
            : square
              ? t('dialog.epitaph.from', { square: squareName(square) })
              : t('dialog.epitaph.fromPawn', { pawn: names.pawn(e.sourcePawnId) });
          const m =
            links.length +
            patternOf(
              links.map((l) => l.face),
              constants,
            ).bonus;
          return (
            <li key={e.id} className="epitaph-option">
              <CardView size="md" epitaph={e.inscription} face={links.at(-1)?.face} label="" />
              <div className="epitaph-option-text">
                <span className="epitaph-origin">{origin}</span>
                <RouteSteps links={links} owner={e.owner} />
                <span className="dim">
                  {t('dialog.epitaph.worth', { m, score: e.inscription.recordedScore })}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                data-autofocus={i === 0 || undefined}
                aria-label={t('dialog.epitaph.keepLabel', { origin })}
                onClick={() => onKeep(e.id)}
              >
                {t('action.keepThis')}
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            {t('action.cancel')}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            data-autofocus
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p>{body}</p>
    </Modal>
  );
}

export function PileDialog({
  v,
  names,
  onClose,
}: {
  v: MatchView;
  names: Names;
  onClose: () => void;
}) {
  return (
    <Modal title={t('match.pile')} onClose={onClose} wide>
      <ol className="pile-list">
        {[...v.pile].reverse().map((e, i) => (
          <li key={i}>
            <PileCard entry={e} size="sm" />
            <span>
              {pileEntryLabel(e)} · {names.side(e.playedBy)}
              {i === 0 && <span className="chip chip-top">{t('match.top')}</span>}
            </span>
          </li>
        ))}
      </ol>
    </Modal>
  );
}

function CardList({ v, ids }: { v: MatchView; ids: readonly string[] }) {
  return ids.length === 0 ? (
    <p className="dim">—</p>
  ) : (
    <ul className="zone-list">
      {ids.map((id) => {
        const c = v.cards[id] as Card | undefined;
        return (
          <li key={id}>
            <CardView
              size="sm"
              face={c?.face ?? null}
              wild={c?.kind === 'wild'}
              label={cardLong(c)}
            />
          </li>
        );
      })}
    </ul>
  );
}

/** Everything about one player: scores, Epitaph, reserves, and the cards spent and removed. */
export function PlayerDialog({
  v,
  side,
  names,
  flipRoute,
  marked,
  onClose,
}: {
  v: MatchView;
  side: Side;
  names: Names;
  flipRoute: boolean;
  marked?: ReadonlySet<string>;
  onClose: () => void;
}) {
  const p = v.players[side];
  const c = v.config.constants;
  const headStart = side === 'N' ? v.config.northHeadStart : 0;
  const epitaph = p.epitaphs[0];
  // Long Night: the side's kit, each thing tappable, its sheet stepping through the rest.
  const ledger = v.ledger;
  const kit = kitItems(v, side);
  const sheet = useBrowser(kit);
  const showKit = (uid: string) =>
    sheet.show(
      Math.max(
        0,
        kit.findIndex((k) => k.uid === uid),
      ),
    );
  return (
    <Modal title={names.side(side)} onClose={onClose} wide>
      <dl className="player-stats">
        <div>
          <dt>{t('match.roundScore')}</dt>
          <dd>
            {p.roundScore}
            {headStart > 0 && (
              <span className="dim small"> ({t('match.headStart', { n: headStart })})</span>
            )}
          </dd>
        </div>
        <div>
          <dt>{t('match.total')}</dt>
          <dd>{p.totalScore}</dd>
        </div>
        <div>
          <dt>{t('match.roundWins')}</dt>
          <dd>
            <WinPips won={p.roundWins} of={c.ROUND_WINS_NEEDED} />
            <span className="sr-only">
              {t('match.winsOf', { n: p.roundWins, of: c.ROUND_WINS_NEEDED })}
            </span>
          </dd>
        </div>
        <div>
          <dt>{t('match.hand')}</dt>
          <dd>{p.handCount}</dd>
        </div>
        <div>
          <dt>{t('match.deck')}</dt>
          <dd>{p.deckCount}</dd>
        </div>
        {p.passed && (
          <div>
            <dt>{t('player.thisRound')}</dt>
            <dd>{t('match.passed')}</dd>
          </div>
        )}
      </dl>
      {ledger && (
        <section className="player-kit">
          <h3 className="mini-label">
            {t('ledger.mantel', {
              n: ledger.mantel[side].length,
              slots: v.config.ledger?.kits[side].slots ?? 5,
            })}
          </h3>
          <Mantel
            items={ledger.mantel[side]}
            slots={v.config.ledger?.kits[side].slots ?? 5}
            size="sheet"
            onOpen={(item) => showKit(item.uid)}
          />
          {ledger.curios[side].length > 0 && (
            <>
              <h3 className="mini-label">{t('ledger.curios')}</h3>
              <div className="player-curios">
                {ledger.curios[side].map((c) => (
                  <button
                    key={c.uid}
                    type="button"
                    className="player-curio"
                    onClick={() => showKit(c.uid)}
                  >
                    <NightObject spec={{ kind: 'curio', emblem: c.id }} size={40} />
                    <span>{kit.find((k) => k.uid === c.uid)?.show.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      )}
      <h3 className="mini-label">{t('epitaph.slot')}</h3>
      {epitaph?.inscription.veiled ? (
        <p className="dim">{t('epitaph.veiled')}</p>
      ) : epitaph ? (
        <div className="epitaph-slot">
          <CardView
            size="sm"
            epitaph={epitaph.inscription}
            face={epitaph.inscription.links[epitaph.inscription.links.length - 1]?.face ?? null}
            flipRoute={flipRoute}
            label={`${t('card.epitaph')}: ${routeWords(epitaph.inscription.links, epitaph.owner)}`}
          />
          <div className="epitaph-meta">
            <RouteSteps links={epitaph.inscription.links} owner={epitaph.owner} />
            <span className="dim">
              {t('epitaph.score', { score: epitaph.inscription.recordedScore })}
            </span>
            <span className="dim">{names.pawn(epitaph.sourcePawnId)}</span>
          </div>
        </div>
      ) : (
        <p className="dim">{t('epitaph.empty')}</p>
      )}
      {p.epitaphPlays > 0 && <p className="badge">{t('match.epitaphPlayed')}</p>}
      {p.reserves.length > 0 && (
        <>
          <h3 className="mini-label">{t('match.reserves')}</h3>
          <ul className="reserve-list">
            {p.reserves.map((id) => (
              <li key={id} className={p.reservesPawnedThisRound.includes(id) ? 'dim' : ''}>
                {names.pawn(id)}
                {p.reservesPawnedThisRound.includes(id) ? ` (${t('match.reserveUsed')})` : ''}
                {(marked?.has(id) || p.collateralUsed.includes(id)) && side === 'S' ? (
                  <span
                    className="ticket-inline"
                    role="img"
                    aria-label={t('pawn.marked')}
                    title={t('pawn.marked')}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}
      <h3 className="mini-label">
        {t('match.spent')} ({p.spent.length})
      </h3>
      <CardList v={v} ids={p.spent} />
      <h3 className="mini-label">
        {t('match.removed')} ({p.removed.length})
      </h3>
      <CardList v={v} ids={p.removed} />
      {sheet.open && (
        <ItemSheet
          item={sheet.open.show}
          actions={[]}
          browse={sheet.browse}
          onClose={sheet.close}
        />
      )}
    </Modal>
  );
}

export function RoundEndDialog({
  round,
  names,
  onClose,
}: {
  round: RoundSummary;
  names: Names;
  onClose: () => void;
}) {
  return (
    <Modal
      title={t('dialog.roundEnd.title', { round: round.round })}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" data-autofocus onClick={onClose}>
          {t('action.next')}
        </button>
      }
    >
      <p className="round-result">
        {round.winner
          ? t('dialog.roundEnd.winner', {
              name: names.side(round.winner),
              w: round.scores[round.winner],
              l: round.scores[round.winner === 'S' ? 'N' : 'S'],
            })
          : t('dialog.roundEnd.tie', { s: round.scores.S })}
      </p>
    </Modal>
  );
}

/**
 * A player the game passed for, having nothing left to play (§6.1): told before the round's (or
 * the match's) result, or when it is this device's player, so a round never ends unexplained.
 */
export interface NoMove {
  readonly side: Side;
  readonly kind: 'forced' | 'automatic' | 'outOfTurns';
  /** Out of turns: the rule that ran out, and the turns it allows a round. */
  readonly limit?: { readonly rule: string; readonly n: number };
  /** The card drawn just before, the deck's last (null when it is hidden from this player). */
  readonly drew?: Card | null;
  /** The round that ended with it, if it did. */
  readonly roundOver: number | null;
  /** That round's result in a line ("Old Tallow wins 120–80"): told here, not in a dialog after. */
  readonly roundResult?: string;
  readonly matchOver: boolean;
}

export function NoMoveDialog({
  noMove,
  names,
  onClose,
}: {
  noMove: NoMove;
  names: Names;
  onClose: () => void;
}) {
  const name = names.side(noMove.side);
  const other = names.side(noMove.side === 'S' ? 'N' : 'S');
  const drew = noMove.drew;
  return (
    <Modal
      title={t(noMove.kind === 'outOfTurns' ? 'dialog.noMove.titleTurns' : 'dialog.noMove.title')}
      className="no-move"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" data-autofocus onClick={onClose}>
          {noMove.matchOver ? t('dialog.noMove.result') : t('action.next')}
        </button>
      }
    >
      <p>{t('dialog.noMove.lead', { name })}</p>
      {drew && (
        <div className="no-move-card">
          <CardView
            size="sm"
            face={drew.face}
            wild={drew.kind === 'wild'}
            state="unplayable"
            label={cardLong(drew)}
          />
        </div>
      )}
      <ul className="no-move-reasons">
        {noMove.kind === 'outOfTurns' ? (
          <li>
            {t('dialog.noMove.outOfTurns', {
              name,
              rule: noMove.limit?.rule ?? '',
              n: noMove.limit?.n ?? '',
            })}
          </li>
        ) : noMove.kind === 'automatic' ? (
          <li>{t('dialog.noMove.noPawns')}</li>
        ) : (
          <>
            {drew !== undefined && (
              <li>
                {drew
                  ? t('dialog.noMove.drew', { name, card: cardLong(drew) })
                  : t('dialog.noMove.drewHidden', { name })}
              </li>
            )}
            <li>{t('dialog.noMove.noCard')}</li>
            <li>{t('dialog.noMove.noDeck')}</li>
          </>
        )}
      </ul>
      <p className="no-move-then">
        {noMove.roundOver === null
          ? t('dialog.noMove.playsOn', { name: other })
          : t(noMove.matchOver ? 'dialog.noMove.matchOver' : 'dialog.noMove.roundOver', {
              round: noMove.roundOver,
            })}
      </p>
      {noMove.roundResult && <p className="round-result">{noMove.roundResult}</p>}
    </Modal>
  );
}

export function MatchEndDialog({
  result,
  rounds,
  totals,
  names,
  viewerWon,
  onSaveReplay,
  onExport,
  onRematch,
  onExit,
  exitLabel,
  onShare,
  extra,
}: {
  result: MatchResult;
  rounds: readonly RoundSummary[];
  totals: Readonly<Record<Side, number>>;
  names: Names;
  viewerWon: boolean | null;
  onSaveReplay?: () => string | null;
  onExport?: () => void;
  onRematch?: () => void;
  onExit: () => void;
  exitLabel: string;
  onShare?: () => Promise<ShareResult>;
  /** Shown under the scores (online games: the rating change). */
  extra?: ReactNode;
}) {
  const [saved, setSaved] = useState<string | null>(null);
  return (
    <Modal
      title={t('dialog.matchEnd.title')}
      wide
      className={viewerWon === true ? 'modal-win' : viewerWon === false ? 'modal-loss' : ''}
      footer={
        <>
          {onSaveReplay && (
            <button
              type="button"
              className="btn"
              disabled={saved !== null}
              onClick={() => setSaved(onSaveReplay() ?? t('dialog.matchEnd.saved'))}
            >
              {saved ?? t('dialog.matchEnd.saveReplay')}
            </button>
          )}
          {onExport && (
            <button type="button" className="btn" onClick={onExport}>
              {t('dialog.matchEnd.export')}
            </button>
          )}
          {onRematch && (
            <button type="button" className="btn" onClick={onRematch}>
              {t('dialog.matchEnd.rematch')}
            </button>
          )}
          {onShare && <ShareButton create={onShare} label={t('share.replay')} />}
          <button type="button" className="btn btn-primary" data-autofocus onClick={onExit}>
            {exitLabel}
          </button>
        </>
      }
    >
      <p className="match-result">
        {result.winner
          ? t('dialog.matchEnd.winner', { name: names.side(result.winner) })
          : t('dialog.matchEnd.draw')}
      </p>
      <p className="dim">{t(`dialog.matchEnd.reason.${result.reason}`)}</p>
      <table className="score-table">
        <thead>
          <tr>
            <th scope="col" />
            {rounds.map((r) => (
              <th key={r.round} scope="col">
                {t('match.roundN', { round: r.round })}
              </th>
            ))}
            <th scope="col">{t('match.total')}</th>
          </tr>
        </thead>
        <tbody>
          {(['S', 'N'] as const).map((side) => (
            <tr key={side}>
              <th scope="row">{names.side(side)}</th>
              {rounds.map((r) => (
                <td key={r.round} className={r.winner === side ? 'won' : ''}>
                  {r.scores[side]}
                </td>
              ))}
              <td>{totals[side]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {extra}
    </Modal>
  );
}

export function HandoffOverlay({
  side,
  name,
  onReady,
}: {
  side: Side;
  name: string;
  onReady: () => void;
}) {
  return (
    <div className="handoff" role="dialog" aria-modal="true" aria-labelledby="handoff-title">
      <div className="handoff-card">
        <span className="handoff-piece" aria-hidden="true">
          <PawnFigure side={side} crowned={false} immune={false} inscription={null} />
        </span>
        <h2 id="handoff-title">{t('dialog.handoff.title')}</h2>
        <p>{t('dialog.handoff.body', { name, side: t(`side.${side}`) })}</p>
        <button type="button" className="btn btn-primary" autoFocus onClick={onReady}>
          {t('dialog.handoff.ready', { name })}
        </button>
      </div>
    </div>
  );
}
